import type {
  BulkPublishResponse,
  BulkPublishResponseSkippedItemReason,
  ProductSummaryStatus,
  StoreProductStatus,
} from '@nucleus-commerce/api-client/admin';
import { ApiError } from '@nucleus-commerce/api-client/transport';

export const STORE_PRODUCT_STATUSES: StoreProductStatus[] = ['draft', 'published', 'unpublished'];

export const SKIP_REASONS: BulkPublishResponseSkippedItemReason[] = [
  'not_active',
  'handle_taken',
  'not_found',
];

/** Only an active master product may go live; anything else lands in the store as a draft. */
export function publicationStatusFor(masterStatus: ProductSummaryStatus): StoreProductStatus {
  return masterStatus === 'active' ? 'published' : 'draft';
}

/** 409 ALREADY_EXISTS on `body.handle`: another product of the store has this handle. */
export function handleTaken(error: unknown): boolean {
  return (
    error instanceof ApiError &&
    error.code === 'ALREADY_EXISTS' &&
    error.details.some((detail) => detail.field === 'body.handle')
  );
}

/** 409 CONFLICT with `meta.productStatus`: the master product is not active. */
export function publishConflict(error: unknown): { productStatus: string } | null {
  if (!(error instanceof ApiError) || error.code !== 'CONFLICT') return null;
  const status = error.meta.productStatus;
  return typeof status === 'string' ? { productStatus: status } : null;
}

/** 409 ALREADY_EXISTS on `body.productId`: the product is already in the store, `meta.storeProductId` says where. */
export function alreadyInStore(error: unknown): string | null {
  if (!(error instanceof ApiError) || error.code !== 'ALREADY_EXISTS') return null;
  const id = error.meta.storeProductId;
  return typeof id === 'string' ? id : null;
}

/** Skipped products grouped by reason so the summary reads "2 not active, 1 handle taken". */
export function groupSkipped(
  result: BulkPublishResponse,
): Array<{ reason: BulkPublishResponseSkippedItemReason; productIds: string[] }> {
  return SKIP_REASONS.flatMap((reason) => {
    const productIds = result.skipped.filter((s) => s.reason === reason).map((s) => s.productId);
    return productIds.length > 0 ? [{ reason, productIds }] : [];
  });
}
